import {
	Component,
	OnInit,
	OnDestroy,
	Output,
	EventEmitter,
	Input,
	ViewChild,
	ElementRef,
	AfterContentInit,
	HostListener,
} from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import moment from 'moment';

import { HelperService } from '@app/core/services/helper.service';
import { HasRequiredField } from '@app/ui-forms/classes/has-required-field';

interface IDate {
	date: string;
	shortDate: string;
	weekDay: string;
}

@Component({
	selector: 'ui-date-picker-control',
	templateUrl: './date-picker-control.component.html',
	styleUrls: ['../../styles/form-controls.less', './date-picker-control.component.less'],
})
export class DatePickerControlComponent implements OnInit, OnDestroy, AfterContentInit {
	@Input() form: FormGroup;
	@Input() form_control: FormControl;
	@Input() label: string = null;
	@Input() info: string = null;
	@Input() input_css_class: string = '';
	@Input() disabled: boolean = false;
	@Input() show_error_text: boolean = true;
	@Input() highlighted_info: boolean = false;
	@Input() max_visible_days: number = 14;
	@Input() min_date: string;
	@Input() start_date: string;
	@Input() unavailable_days?: string[] = [];
	@Input() unavailable_dates?: string[] = [];

	@Output() changed: EventEmitter<IDate> = new EventEmitter<IDate>();

	@ViewChild('dateList', { static: true }) date_list: ElementRef;

	form_control_id: string = '';
	form_control_name: string = '';

	dates = [];
	slide_direction = null;

	private destroyed$: Subject<any> = new Subject();
	private slide_timeout: any;

	constructor(private helper_service: HelperService) {}

	ngOnInit(): void {
		let form_control_name = null;
		const parent = this.form_control.parent;

		if (parent instanceof FormGroup) {
			Object.keys(parent.controls).forEach((name) => {
				if (this.form_control === parent.controls[name]) {
					form_control_name = name;
				}
			});
		}

		this.form_control_name = form_control_name;
		this.form_control_id = 'date_picker_' + this.helper_service.generateRandomString(30);
	}

	ngAfterContentInit(): void {
		this.setDates(this.start_date ? moment(this.start_date) : null);
	}

	onSelectDate(date): void {
		if (this.unavailable_dates.includes(date)) {
			return;
		}
		if (this.unavailable_days.includes(moment(date).format('dddd').toLowerCase())) {
			return;
		}
		this.form_control.setValue(date);
		this.changed.emit(date);
	}

	next() {
		const newDate = moment(this.dates[0]).add(this.dates.length, 'd').startOf('day');

		if (this.form_control.value) {
			this.onSelectDate(newDate.format('yyyy-MM-DD'));
		}

		this.setDates(newDate);
		this.slideDateList('left');
	}

	previous() {
		let newDate = moment(this.dates[0]).add(-this.dates.length, 'd').startOf('day');

		if (this.min_date && newDate.diff(this.min_date) < 0) {
			newDate = moment(this.min_date);
		}

		if (this.form_control.value) {
			this.onSelectDate(newDate.format('yyyy-MM-DD'));
		}

		this.setDates(newDate);
		this.slideDateList('right');
	}

	slideDateList(direction): void {
		clearTimeout(this.slide_timeout);
		this.slide_direction = 'slider-' + direction;
		this.slide_timeout = setTimeout(() => {
			this.slide_direction = null;
		}, 200);
	}

	@HostListener('window:resize')
	setDates(startDate = null) {
		if (!startDate) {
			if (this.form_control.value) {
				startDate = moment(this.form_control.value).startOf('day');
			} else if (this.dates.length) {
				startDate = moment(this.dates[0]).startOf('day');
			} else {
				startDate = moment().startOf('day');
			}
		}
		const availableSpace = this.date_list.nativeElement.offsetWidth;
		const spaceFor = Math.floor(availableSpace / 50);

		const dates = [];

		for (var i = 0; i < (this.max_visible_days < spaceFor ? this.max_visible_days : spaceFor); i++) {
			dates.push(startDate.format('yyyy-MM-DD'));
			startDate.add(1, 'd');
		}

		this.dates = dates;
	}

	isInvalid(): boolean {
		return (this.form_control.dirty || this.form_control.touched) && this.form_control.invalid;
	}

	getError(): string {
		if (this.isInvalid() && this.show_error_text) {
			if (this.form_control.hasError('required')) {
				return 'is required';
			}

			if (this.form_control.hasError('isBlank')) {
				return 'is blank';
			}

			return 'is invalid';
		}
	}

	getRequired(): string {
		if (!this.getError()) {
			return HasRequiredField(this.form_control) ? ' *' : '';
		}
	}

	ngOnDestroy() {
		this.destroyed$.next();
	}
}
